import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next"; // I18N
import { Link } from "react-router-dom";
import axios from "axios";
import SectionTitle from "../SectionTitle/SectionTitle";

const OrganisationsSection = ({
  hclass = "",
  limit = null,
  showTitle = true,
}) => {
  const { t } = useTranslation(); // I18N
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  useEffect(() => {
    fetchOrganisations();
  }, [currentPage]);

  const fetchOrganisations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || "https://api.caprover.sokaab.com/api"}/public/organisations`,
        {
          params: {
            skip: currentPage,
            take: limit || itemsPerPage,
          },
        },
      );
      setOrganisations(response.data.list || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      console.error("Error fetching organisations:", err);
      setError("failedToLoad"); // I18N — stored as key, translated at render
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const calculateProgress = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(100, (raised / goal) * 100);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <section
        className={`wpo-causes-section-s3 section-padding ${hclass}`}
        style={{
          background: "linear-gradient(180deg, #f8fdfb 0%, #eef7f4 100%)",
        }}
      >
        <div className="container">
          <div className="text-center py-5">
            <span
              className="spinner-border"
              role="status"
              style={{ color: "#00CC99" }}
            />
            <p className="mt-2">{t("home.organisations.loading")}</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className={`wpo-causes-section-s3 section-padding ${hclass}`}
        style={{
          background: "linear-gradient(180deg, #f8fdfb 0%, #eef7f4 100%)",
        }}
      >
        <div className="container">
          <div className="text-center py-5">
            <i className="fas fa-exclamation-circle fa-2x text-muted mb-2"></i>
            <p>{t("home.organisations.failedToLoad")}</p>
            <button onClick={fetchOrganisations} className="btn btn-primary">
              {t("home.organisations.tryAgain")}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (organisations.length === 0) {
    return (
      <section
        className={`wpo-causes-section-s3 section-padding ${hclass}`}
        style={{
          background: "linear-gradient(180deg, #f8fdfb 0%, #eef7f4 100%)",
        }}
      >
        <div className="container">
          <div className="text-center py-5">
            <i className="fas fa-building fa-2x text-muted mb-2"></i>
            <p>{t("home.organisations.notFound")}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`wpo-causes-section-s3 section-padding ${hclass}`}
      style={{
        background: "linear-gradient(180deg, #f8fdfb 0%, #eef7f4 100%)",
      }}
    >
      <div className="container">
        {showTitle && (
          <div className="row justify-content-center mb-5">
            <div className="col-lg-8 col-md-10 col-12 text-center">
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <span
                  style={{
                    width: "40px",
                    height: "2px",
                    background: "linear-gradient(90deg, transparent, #00CC99)",
                  }}
                ></span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#00CC99",
                    textTransform: "uppercase",
                    letterSpacing: "2px",
                  }}
                >
                  {t("home.organisations.ourPartners")}
                </span>
                <span
                  style={{
                    width: "40px",
                    height: "2px",
                    background: "linear-gradient(90deg, #00CC99, transparent)",
                  }}
                ></span>
              </div>
              <p
                style={{
                  fontSize: "18px",
                  lineHeight: "1.7",
                  color: "#555",
                  margin: "0",
                  fontWeight: "400",
                }}
              >
                <Trans
                  i18nKey="home.organisations.descriptionBefore"
                  defaults="These trusted organizations collaborate with "
                />
                <span style={{ color: "#00CC99", fontWeight: "600" }}>
                  SOKAAB
                </span>
                <Trans
                  i18nKey="home.organisations.descriptionAfter"
                  defaults=" to support grassroots initiatives and community-led projects across Somalia."
                />
              </p>
            </div>
          </div>
        )}
        <div className="row">
          {organisations.map((org) => {
            return (
              <div
                className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4"
                key={org.organisationId}
              >
                <Link
                  to={`/partners/${org.organisationId}`}
                  style={{ textDecoration: "none" }}
                >
                  <div
                    className="partner-card h-100 text-center"
                    style={{
                      background: "#fff",
                      borderRadius: "16px",
                      padding: "28px 20px",
                      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                      border: "1px solid #f0f0f0",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 40px rgba(0, 204, 153, 0.15)";
                      e.currentTarget.style.borderColor = "#00CC99";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 12px rgba(0, 0, 0, 0.06)";
                      e.currentTarget.style.borderColor = "#f0f0f0";
                    }}
                  >
                    {/* Logo */}
                    <div className="mb-3">
                      {org.organisationLogo ? (
                        <img
                          src={org.organisationLogo}
                          alt={org.organisationName}
                          style={{
                            width: "72px",
                            height: "72px",
                            objectFit: "cover",
                            borderRadius: "50%",
                            border: "3px solid #f8f9fa",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "72px",
                            height: "72px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #00CC99 0%, #00a67d 100%)",
                            color: "#fff",
                            fontSize: "28px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {org.organisationName?.charAt(0)?.toUpperCase() ||
                            "O"}
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <h6
                      style={{
                        color: "#1a1a1a",
                        fontSize: "15px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        lineHeight: "1.4",
                        minHeight: "42px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {org.organisationName?.length > 35
                        ? org.organisationName.substring(0, 35) + "..."
                        : org.organisationName}
                    </h6>

                    {/* Country */}
                    {org.country && (
                      <p
                        style={{
                          color: "#888",
                          fontSize: "12px",
                          marginBottom: "16px",
                        }}
                      >
                        <i
                          className="fas fa-map-marker-alt"
                          style={{ marginRight: "4px", color: "#00CC99" }}
                        ></i>
                        {org.country}
                      </p>
                    )}

                    {/* Stats Row */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "20px",
                        paddingTop: "16px",
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "700",
                            color: "#00CC99",
                          }}
                        >
                          {org.totalProjects}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {t("home.organisations.projects")}
                        </div>
                      </div>
                      <div
                        style={{
                          width: "1px",
                          background: "#eee",
                        }}
                      ></div>
                      <div>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "700",
                            color: "#1976d2",
                          }}
                        >
                          {org.activeProjects}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {t("home.organisations.active")}
                        </div>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <div
                      style={{
                        marginTop: "16px",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          padding: "10px 20px",
                          background:
                            "linear-gradient(135deg, #00CC99 0%, #00a67d 100%)",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#fff",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("home.organisations.viewDetails")}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {!limit && totalPages > 1 && (
          <div className="row mt-4">
            <div className="col-12">
              <nav aria-label={t("home.organisations.pageNavigation")}>
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      {t("home.organisations.previous")}
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                          style={
                            currentPage === page
                              ? {
                                  backgroundColor: "#00CC99",
                                  borderColor: "#00CC99",
                                }
                              : {}
                          }
                        >
                          {page}
                        </button>
                      </li>
                    ),
                  )}
                  <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      {t("home.organisations.next")}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrganisationsSection;
